'use client'

import "./tabs.css";

import { memo } from 'react'
import { X } from 'lucide-react'
import { getBlockComponent, getResolvedBlockMeta } from '@/lib/notebook/registry/block-components'
import { blockHasSemanticPaste, usePaste } from '@/lib/notebook/core/usePaste'
import { LazyBlockSurface } from '@/lib/notebook/Blocks/shared/LazyBlockSurface'
import { BlockPresentationProvider } from '../shared/composition'
import { BLOCK_PRESENTATION } from '../shared/composition/constants'

const TAILWIND_BLOCK_COMPONENTS = new Set([
  'SummaryBlock',
  'InsightBlock',
  'QuestionBlock',
  'ReferenceBlock',
  'TimelineBlock',
  'MediaBlock',
])

function TabChildBlockInner({ block, locked, onChange, onRemove }) {
  const { resolved, entry } = getResolvedBlockMeta(block.type)
  const Comp = getBlockComponent(block.type)
  const canPaste =
    Boolean(onChange) && !locked && blockHasSemanticPaste(resolved)
  const { onPasteCapture } = usePaste(
    resolved,
    block.id,
    block.data ?? {},
    onChange,
    canPaste,
  )

  if (!entry || !Comp) return null

  const removeBtn = !locked && (
    <button
      type="button"
      className="tabs-child-block__remove"
      onClick={onRemove}
      aria-label="Remove block"
    >
      <X size={12} strokeWidth={2} />
    </button>
  )

  const presentation =
    entry.presentation === BLOCK_PRESENTATION.STANDALONE
      ? BLOCK_PRESENTATION.STANDALONE
      : BLOCK_PRESENTATION.EMBEDDED

  const blockBody =
    entry.component === 'RichText' ? (
      <Comp
        data={block.data ?? {}}
        onChange={onChange}
        locked={locked}
      />
    ) : TAILWIND_BLOCK_COMPONENTS.has(entry.component) ? (
      <div className="nb-tw-block nb-tw-block--tab">
        <Comp data={block.data ?? {}} onChange={onChange} locked={locked} />
      </div>
    ) : (
      <Comp data={block.data ?? {}} onChange={onChange} locked={locked} />
    )

  return (
    <div
      className="tabs-child-block group"
      data-block-type={resolved}
      data-section-id={block.id}
      onPasteCapture={canPaste ? onPasteCapture : undefined}
    >
      <div className="tabs-child-block__body">
        <BlockPresentationProvider mode={presentation}>
          <LazyBlockSurface hostClassName="tabs-child-block__body">
            {blockBody}
          </LazyBlockSurface>
        </BlockPresentationProvider>
      </div>
      {removeBtn}
    </div>
  )
}

const TabChildBlock = memo(TabChildBlockInner)
export default TabChildBlock
